/* oxlint-disable no-void */
import React, { type FocusEvent, forwardRef, useCallback, useRef } from 'react';
import { di } from 'react-magnetic-di';
import { useInlineField } from 'view/common/forge-custom-fields/controllers/store';
import {
	FIELD_STATE_FOCUSED,
	FIELD_STATE_FAILED,
	FIELD_STATE_PENDING,
} from 'view/common/forge-custom-fields/controllers/store/constants';
import type { RenderFn } from '@atlassian/forge-ui-types/components';

type RenderFnProps = Parameters<RenderFn>[0];
export interface Props extends RenderFnProps {
	fieldId: string;
}

export const CustomFieldEdit = ({ forgeDoc, render, fieldId }: Props) => {
	di(useInlineField);

	const [fieldState, { setFieldState }] = useInlineField(fieldId);
	const containerRef = useRef<HTMLDivElement | null>(null);

	const isBlurEventTriggeredOutsideTheField = useCallback(
		(e: FocusEvent<HTMLElement>) => {
			return (
				fieldState?.status === FIELD_STATE_FOCUSED &&
				!containerRef.current?.contains(e.relatedTarget)
			);
		},
		[fieldState],
	);

	const submitOnBlur = useCallback(
		(e: FocusEvent<HTMLElement>) => {
			const { disableSubmitOnBlur, onSubmit } = forgeDoc.props || {};
			if (disableSubmitOnBlur) return;
			try {
				const shouldSubmit = isBlurEventTriggeredOutsideTheField(e);
				void setFieldState({ fieldId, status: FIELD_STATE_PENDING });
				if (shouldSubmit && typeof onSubmit === 'function') {
					(onSubmit as () => void)();
				}
			} catch (error) {
				void setFieldState({ fieldId, status: FIELD_STATE_FAILED });
			}
		},
		[fieldId, forgeDoc.props, isBlurEventTriggeredOutsideTheField, setFieldState],
	);

	const handleFocus = useCallback(() => {
		void setFieldState({ fieldId, status: FIELD_STATE_FOCUSED });
	}, [fieldId, setFieldState]);

	return (
		<InteractiveComponent ref={containerRef} onBlur={submitOnBlur} onFocus={handleFocus}>
			{forgeDoc.children.map(render)}
		</InteractiveComponent>
	);
};

const InteractiveComponent = forwardRef<
	HTMLDivElement,
	{
		children: React.ReactNode;
		onBlur: (e: FocusEvent<HTMLElement>) => void;
		onFocus: () => void;
	}
>(({ children, ...restProps }, ref) => {
	return (
		<div tabIndex={-1} role="group" ref={ref} {...restProps}>
			{children}
		</div>
	);
});

InteractiveComponent.displayName = 'ElementWithEvents';
